import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRightIcon } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Frame, FrameFooter, FrameHeader, FramePanel, FrameTitle, FrameCount } from "@/components/ui/frame";
import { RelativeTime } from "@/components/dashboard_sourcing/RelativeTime";
import { FUNNEL_STEPS, GOAL_LABEL, VERDICT, alertText, clientHealth, fmtUnit, money, pct } from "./format";
import { Hint, SeverityIcon, ToneBadge } from "./parts";
import type { CampaignState, ClientState, GoalState, MailboxState } from "./types";

const LEVEL_RANK = { critical: 0, warn: 1, info: 2 } as const;

/** Everything behind a client card: alerts, the goal's arithmetic, each campaign, the plan's notes. */
export function ClientDetailsSheet({
  client,
  open,
  onOpenChange,
}: {
  client: ClientState | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl">
        {client && <Details client={client} />}
      </SheetContent>
    </Sheet>
  );
}

function Details({ client }: { client: ClientState }) {
  const health = clientHealth(client);
  const alerts = [...client.alerts].sort((a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level]);
  const campaigns = [...client.campaigns].sort((a, b) => Number(b.running) - Number(a.running));
  const minDays = client.plan?.mailbox_health.min_days ?? 7;

  return (
    <>
      <SheetHeader className="border-b pr-12">
        <div className="flex flex-wrap items-center gap-2">
          <SheetTitle className="text-lg">{client.name}</SheetTitle>
          <ToneBadge tone={health.tone}>{health.label}</ToneBadge>
        </div>
        <SheetDescription className="text-xs">
          {client.org ? `${client.org.plan_name ?? "No plan"} · ${client.org.activated_used}/${client.org.activated_limit ?? "∞"} approvals used · ` : ""}
          {client.env} · computed <RelativeTime at={client.computed_at} />
        </SheetDescription>
      </SheetHeader>

      <div className="flex flex-col gap-6 p-4">
        {alerts.length > 0 && (
          <Section title="Alerts">
            <FramePanel className="p-3">
              <ul className="flex flex-col gap-2">
                {alerts.map((a, i) => (
                  <li key={`${a.code}-${i}`} className="flex items-start gap-2 text-sm">
                    <SeverityIcon level={a.level} className="mt-0.5" />
                    <span>{alertText(client, a)}</span>
                  </li>
                ))}
              </ul>
            </FramePanel>
          </Section>
        )}

        {client.goal && (
          <Section title="Goal">
            <FramePanel className="p-3">
              <GoalFacts goal={client.goal} />
            </FramePanel>
          </Section>
        )}

        {campaigns.length > 0 && (
          <Section title="Campaigns" count={campaigns.length}>
            {campaigns.map((c) => (
              <CampaignBlock key={c.campaign_id} campaign={c} minDays={minDays} />
            ))}
          </Section>
        )}

        {client.plan && (
          <Section title="Plan notes">
            <FramePanel className="p-3">
              <PlanNotes body={client.plan.body} notionPages={client.plan.notion_pages ?? []} />
            </FramePanel>
            <FrameFooter>
              {client.plan.version ? (
                <>
                  Plan v{client.plan.version} (
                  <Link to={`/manager/activity?tab=plans&client=${encodeURIComponent(client.slug)}`} className="font-medium text-foreground hover:underline">
                    history
                  </Link>
                  ) ·{" "}
                </>
              ) : null}
              Mailboxes judged after {client.plan.mailbox_health.min_days} days and{" "}
              {client.plan.mailbox_health.min_sends} matured sends
              {client.plan.sizing.max_roster ? ` · roster cap ${client.plan.sizing.max_roster}` : ""}
            </FrameFooter>
          </Section>
        )}
      </div>
    </>
  );
}

/** One titled block of the sheet; the sheet makes it a flat Frame. Children bring their own panel. */
function Section({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>
          {title}
          {count !== undefined && <FrameCount>{count}</FrameCount>}
        </FrameTitle>
      </FrameHeader>
      {children}
    </Frame>
  );
}

function GoalFacts({ goal }: { goal: GoalState }) {
  const rows: [string, ReactNode][] = [
    [GOAL_LABEL[goal.kind], goal.target ? `${fmtUnit(goal, goal.progress)} of ${fmtUnit(goal, goal.target)}` : fmtUnit(goal, goal.progress)],
    ["Target", goal.target ? `${fmtUnit(goal, goal.target)} · ${goal.target_source === "grade_funding" ? "deposited wallet total" : "plan"}` : "none — no pace alarm"],
    [goal.anchor === "rolling" ? "Expected per window" : "Expected by today", goal.expected_now !== null ? fmtUnit(goal, goal.expected_now) : "—"],
    [
      "Window",
      goal.anchor === "rolling"
        ? `last ${goal.window_days} days (rolling)`
        : `${goal.day !== null ? `day ${Math.min(goal.day, goal.window_days)} of ` : ""}${goal.window_days} · ${goal.anchor.replace(/_/g, " ")}${goal.anchor_at ? ` since ${new Date(goal.anchor_at).toLocaleDateString()}` : ""}`,
    ],
    ["In this window", fmtUnit(goal, goal.progress_window)],
    ["All time", fmtUnit(goal, goal.progress_total)],
  ];
  return (
    <div className="flex flex-col gap-2">
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="font-mono tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-muted-foreground">{goal.basis}</p>
    </div>
  );
}

function campaignState(c: CampaignState): { tone: "success" | "muted"; label: string } {
  if (c.running) return c.in_active_hours ? { tone: "success", label: "Sending now" } : { tone: "muted", label: "Outside hours" };
  if (c.status === "OUTREACH") return { tone: "muted", label: "Paused" };
  if (c.status === "SEARCH") return { tone: "muted", label: "Not launched" };
  return { tone: "muted", label: (c.status ?? "—").toLowerCase() };
}

function CampaignBlock({ campaign: c, minDays }: { campaign: CampaignState; minDays: number }) {
  const state = campaignState(c);
  return (
    <FramePanel className={cn("p-0", !c.running && "opacity-80")}>
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2.5">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{c.name}</div>
          <div className="text-xs text-muted-foreground">
            {c.platform ?? "—"} · {c.roster} creators
          </div>
        </div>
        <ToneBadge tone={state.tone}>{state.label}</ToneBadge>
      </div>
      <div className="flex flex-col gap-4 p-3">
        <Funnel funnel={c.funnel} />
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Fact label="Queue" value={String(c.to_contact)} />
          <Fact
            label="Runway"
            value={c.runway_days === null ? (c.to_contact > 0 ? "blocked" : "—") : `${c.runway_days} d`}
            sub={`${c.cold_capacity_per_day} cold/day`}
            warn={c.to_contact > 0 && (c.runway_days === null || c.runway_days < 3)}
          />
          <Fact label="1st follow-ups" value={c.first_followups_owed === null ? "—" : String(c.first_followups_owed)} />
          <Fact
            label="Committed"
            value={c.committed_usd ? money.format(c.committed_usd) : "—"}
            sub={c.has_payout_slot ? "before fees" : "no payout slot"}
          />
        </dl>
        {(c.creator_refresh?.launch_error || c.creator_refresh?.error) && (
          <p className="text-xs text-destructive">
            {c.creator_refresh.launch_error ? `Launch failed: ${c.creator_refresh.launch_error}` : `Creator refresh failed: ${c.creator_refresh.error}`}
          </p>
        )}
        {c.mailboxes.length > 0 && <MailboxTable mailboxes={c.mailboxes} minDays={minDays} />}
      </div>
    </FramePanel>
  );
}

function Fact({ label, value, sub, warn }: { label: string; value: string; sub?: string; warn?: boolean }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn("font-mono text-sm font-medium tabular-nums", warn && "text-warning")}>{value}</dd>
      {sub && <dd className="text-[11px] text-muted-foreground">{sub}</dd>}
    </div>
  );
}

function Funnel({ funnel }: { funnel: CampaignState["funnel"] }) {
  const steps = FUNNEL_STEPS.map((s) => ({ ...s, n: s.groups.reduce((sum, g) => sum + (funnel[g] ?? 0), 0) })).filter((s) => s.n > 0);
  if (steps.length === 0) return <p className="text-xs text-muted-foreground">No creators yet.</p>;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-2.5 w-full gap-px overflow-hidden rounded-pill" role="img" aria-label={steps.map((s) => `${s.label} ${s.n}`).join(", ")}>
        {steps.map((s) => (
          <div key={s.key} className={cn("min-w-1", s.color)} style={{ flex: `${s.n} 1 0%` }} />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
        {steps.map((s) => (
          <li key={s.key} className="inline-flex items-center gap-1.5">
            <span className={cn("size-2 rounded-[2px]", s.color)} />
            <span className="text-muted-foreground">{s.label}</span>
            <span className="font-mono">{s.n}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MailboxTable({ mailboxes, minDays }: { mailboxes: MailboxState[]; minDays: number }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Mailbox</TableHead>
          <TableHead className="text-right">
            <Hint text={`Replies among creators cold-emailed at least ${minDays} days ago — younger sends have not had time to be answered`}>Matured rate</Hint>
          </TableHead>
          <TableHead className="text-right">Replied</TableHead>
          <TableHead className="text-right">Days</TableHead>
          <TableHead>Verdict</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {mailboxes.map((m) => (
          <TableRow key={m.account_id}>
            <TableCell className="max-w-56">
              <div className="flex items-center gap-2">
                <span className={cn("truncate", !m.enabled && "text-muted-foreground")}>{m.email ?? m.account_id}</span>
                {!m.enabled && (
                  <Hint text="Switched off: sends nothing unsolicited, still answers the creators who replied">
                    <span className="shrink-0 text-[11px] text-muted-foreground">off</span>
                  </Hint>
                )}
              </div>
            </TableCell>
            <TableCell className="text-right font-mono">
              {pct(m.matured_rate)}
              <span className="ml-1.5 text-[11px] text-muted-foreground">
                {m.matured_replies}/{m.matured_sends}
              </span>
            </TableCell>
            <TableCell className="text-right font-mono">
              {m.replied}/{m.contacted}
            </TableCell>
            <TableCell className="text-right font-mono">{m.days_sending ?? "—"}</TableCell>
            <TableCell>
              <Hint text={m.verdict === "immature" ? `Judged only after ${minDays} days of sending and enough matured sends` : VERDICT[m.verdict].hint} underline={false}>
                <span className={cn("inline-flex items-center gap-1.5 text-xs", m.verdict === "weak" && "text-destructive")}>
                  <span className={cn("size-1.5 rounded-full", VERDICT[m.verdict].dot)} />
                  {VERDICT[m.verdict].label}
                </span>
              </Hint>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** The client's prose: links to its Notion pages, plus a plan file's body when there is one (the evals' fixture client). */
function PlanNotes({ body, notionPages }: { body: string; notionPages: { title: string; page_id: string }[] }) {
  const paragraphs = body
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s*\n\s*/g, " ").trim())
    .filter(Boolean);
  if (paragraphs.length === 0 && notionPages.length === 0) return <p className="text-sm text-muted-foreground">No notes for this client.</p>;
  return (
    <div className="flex flex-col gap-2 text-sm leading-relaxed text-foreground/80">
      {paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
      {notionPages.map((page) => (
        <a
          key={page.page_id}
          href={`https://www.notion.so/${page.page_id.replace(/-/g, "")}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex w-fit items-center gap-1 font-medium text-foreground hover:underline"
        >
          {page.title} in Notion
          <ArrowUpRightIcon className="size-3.5" />
        </a>
      ))}
    </div>
  );
}
